import { useCallback, useMemo, useRef, useState } from 'react';
import { Button, message, Modal } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { getUploadList, deleteUpload as deletePolicyUpload, uploadPolicy } from '../../../infrastructure/apiClient';
import NewButton from '../../../components/NewButton/NewButton';
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog';
import CardTable from '../../../components/Table/CardTable';
import { buildFilterOptions, getCheckboxFilterProps } from '../../../components/Table/columnFilterUtils';
import type { PolicyUploadDTO } from '../../../infrastructure/models';
import Upload, { type UploadPayload } from '../Upload/Upload';
import PolicyAnalysis, { type PolicyAnalysisPayload } from '../PolicyAnalysis/PolicyAnalysis';
import { POLICY_CONFIGURATION_BUILDING_BLOCK_MAP } from '../constants/buildingBlocks';
import DashboardLayout, { dashboardStyles as styles } from '../../../components/DashboardLayout/DashboardLayout';
import { getNextPageAfterDelete } from '../../../utils/pagination';
import { useDashboardData } from '../../../hooks/useDashboardData';
import { useDeleteConfirmation } from '../../../hooks/useDeleteConfirmation';
import { sortData, sortConfig, type SortConfig } from '../../../utils/sortUtils';

interface UploadDashboardProps {
  onUploadPolicySelect?: (upload: PolicyUploadDTO) => void;
  onOpenComparisonDashboard?: () => void;
}

const formatRegionLabel = ({ continent, country, state }: Pick<PolicyUploadDTO, 'continent' | 'country' | 'state'>) => {
  const parts = [continent, country, state].filter((part): part is string => Boolean(part && part.trim()));
  return parts.length ? parts.join(' / ') : '—';
};

const SORT_CONFIGS: SortConfig<PolicyUploadDTO>[] = [
  sortConfig('fileName', 'string'),
  sortConfig('configurationType', 'string'),
  sortConfig('locationCode', 'string'),
  sortConfig('region', 'string', (record) => formatRegionLabel(record)),
  sortConfig('status', 'string'),
  sortConfig('userId', 'string'),
  sortConfig('continent', 'string'),
  sortConfig('country', 'string'),
  sortConfig('state', 'string'),
  sortConfig('createdBy', 'string'),
  sortConfig('createdAt', 'date'),
  sortConfig('updatedAt', 'date'),
];

const UploadDashboard = ({ onUploadPolicySelect, onOpenComparisonDashboard }: UploadDashboardProps) => {
  const {
    data: uploads,
    loading,
    setLoading,
    error,
    activeClient,
    tenantsLoading,
    pagination,
    sorter,
    tablePagination,
    handleTableChange,
    updateCurrentPage,
    fetchData: fetchUploads,
    abortControllerRef,
    disableActions,
  } = useDashboardData<PolicyUploadDTO>({
    fetchFn: getUploadList,
    entityName: 'policy uploads',
  });
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [policyAnalysisModalOpen, setPolicyAnalysisModalOpen] = useState(false);

  const [fileNameFilters, setFileNameFilters] = useState<string[]>([]);
  const [typeFilters, setTypeFilters] = useState<string[]>([]);
  const [createdByFilters, setCreatedByFilters] = useState<string[]>([]);
  const deleteInFlight = useRef(false);
  const uploadInFlight = useRef(false);

  const handleDeleteConfiguration = useCallback(async (upload: PolicyUploadDTO) => {
    if (deleteInFlight.current) return;
    if (!activeClient?.id) {
      message.warning('Select a tenant before deleting an upload.');
      return;
    }

    try {
      deleteInFlight.current = true;
      setLoading(true);
      await deletePolicyUpload(activeClient.id, upload.id);
      const { nextPage, offset } = getNextPageAfterDelete({
        currentPage: pagination.current,
        pageSize: pagination.pageSize,
        totalItems: pagination.total,
        deletedItems: 1,
      });
      updateCurrentPage(nextPage);
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      await fetchUploads(activeClient.id, { offset, limit: pagination.pageSize, signal: controller.signal });
      message.success(`Uploaded ${upload.fileName} deleted.`);
    } catch (err) {
      console.error('Failed to delete upload', err);
      message.error('Failed to delete upload.');
    } finally {
      deleteInFlight.current = false;
      setLoading(false);
    }
  }, [activeClient?.id, fetchUploads, pagination.current, pagination.pageSize, pagination.total, updateCurrentPage]);

  const {
    confirmOpen,
    confirmLoading,
    itemToDelete: confirmUpload,
    requestDelete: handleOpenDeleteConfirm,
    cancelDelete: handleCloseDeleteConfirm,
    confirmDelete: handleConfirmDelete,
  } = useDeleteConfirmation<PolicyUploadDTO>(handleDeleteConfiguration);

  const fileNameOptions = useMemo(() => buildFilterOptions(uploads, (r) => r.fileName ?? undefined), [uploads]);
  const typeOptions = useMemo(() => buildFilterOptions(uploads, (r) => r.configurationType ?? undefined), [uploads]);
  const createdByOptions = useMemo(() => buildFilterOptions(uploads, (r) => r.createdBy ?? undefined), [uploads]);

  const columns = useMemo<ColumnsType<PolicyUploadDTO>>(() => [
    {
      title: 'File Name',
      dataIndex: 'fileName',
      key: 'fileName',
      ellipsis: true,
      sorter: (a, b) => (a.fileName ?? '').localeCompare(b.fileName ?? ''),
      sortOrder: sorter.columnKey === 'fileName' ? sorter.order : null,
      ...getCheckboxFilterProps(
        fileNameFilters,
        setFileNameFilters,
        fileNameOptions,
        (record) => record.fileName ?? undefined,
      ),
      render: (value: string, record) => {
        if (!onUploadPolicySelect) {
          return value;
        }
        return (
          <button
            type="button"
            className={styles.nameLink}
            onClick={() => onUploadPolicySelect(record)}
          >
            {value}
          </button>
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'configurationType',
      key: 'configurationType',
      sorter: (a, b) => (a.configurationType ?? '').localeCompare(b.configurationType ?? ''),
      sortOrder: sorter.columnKey === 'configurationType' ? sorter.order : null,
      ...getCheckboxFilterProps(
        typeFilters,
        setTypeFilters,
        typeOptions,
        (record) => record.configurationType ?? undefined,
      ),
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      ellipsis: true,
      sorter: (a, b) => (a.createdBy ?? '').localeCompare(b.createdBy ?? ''),
      sortOrder: sorter.columnKey === 'createdBy' ? sorter.order : null,
      ...getCheckboxFilterProps(
        createdByFilters,
        setCreatedByFilters,
        createdByOptions,
        (record) => record.createdBy ?? undefined,
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      ellipsis: true,
      render: (value: string) => dayjs(value).format('MMM D, YYYY h:mm A'),
      sorter: (a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf(),
      sortOrder: sorter.columnKey === 'createdAt' ? sorter.order : null,
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      ellipsis: true,
      render: (value: string) => dayjs(value).format('MMM D, YYYY h:mm A'),
      sorter: (a, b) => dayjs(a.updatedAt).valueOf() - dayjs(b.updatedAt).valueOf(),
      sortOrder: sorter.columnKey === 'updatedAt' ? sorter.order : null,
    },
    {
      title: 'Delete',
      key: 'delete',
      align: 'center',
      width: 80,
      render: (_value, record) => (
        <button
          type="button"
          className={styles.deleteButton}
          aria-label="Delete upload"
          onClick={() => handleOpenDeleteConfirm(record)}
          disabled={loading || tenantsLoading || !activeClient?.id}
        >
          <DeleteOutlined />
        </button>
      ),
    },
  ], [sorter.columnKey, sorter.order, onUploadPolicySelect, handleDeleteConfiguration, loading, tenantsLoading, activeClient?.id,
    fileNameFilters, fileNameOptions, typeFilters, typeOptions, createdByFilters, createdByOptions]);

  const sortedConfigurations = useMemo(() => sortData(uploads, sorter, SORT_CONFIGS), [uploads, sorter]);

  const handleNewUpload = () => {
    setUploadModalOpen(true);
  };

  const handleCloseUploadModal = () => {
    setUploadModalOpen(false);
  };

  const handleOpenPolicyAnalysisModal = () => {
    setPolicyAnalysisModalOpen(true);
  };

  const handleClosePolicyAnalysisModal = () => {
    setPolicyAnalysisModalOpen(false);
  };

  const handleConfigurationUploadSubmit = async ({ buildingBlockKey, files }: UploadPayload) => {
    if (uploadInFlight.current) return;
    if (!activeClient?.id) {
      message.warning('Select a tenant before submitting a policy upload request.');
      return;
    }

    if (!buildingBlockKey) {
      message.warning('Select a building block before uploading.');
      return;
    }

    if (!files.length) {
      message.warning('Select a document before uploading.');
      return;
    }

    const tenantId = activeClient.id;
    const selectedBlock = POLICY_CONFIGURATION_BUILDING_BLOCK_MAP[buildingBlockKey];
    if (!selectedBlock) {
      message.error('Invalid building block selection.');
      return;
    }

    const configurationType = Number(selectedBlock.code);
    if (Number.isNaN(configurationType)) {
      message.error('Invalid building block selection.');
      return;
    }

    try {
      uploadInFlight.current = true;
      setLoading(true);
      await uploadPolicy(tenantId, configurationType, files);
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      await fetchUploads(tenantId, { offset: 0, limit: pagination.pageSize, signal: controller.signal });
      const successMessage = files.length === 1
        ? 'Policy upload request submitted.'
        : `${files.length} policy upload requests submitted.`;
      message.success(successMessage);
      setUploadModalOpen(false);
    } catch (err) {
      console.error('Failed to submit policy upload request', err);
      message.error('Failed to submit policy upload request.');
    } finally {
      uploadInFlight.current = false;
      setLoading(false);
    }
  };

  const handlePolicyAnalysisSubmit = async (_payload: PolicyAnalysisPayload) => {
    setPolicyAnalysisModalOpen(false);
    message.success('Policy analysis started.');
  };

  return (
    <DashboardLayout
      title="Policy Analysis"
      actions={
        <>
          <NewButton onClick={handleNewUpload} disabled={disableActions} label="New Policy Upload" />
          <NewButton onClick={handleOpenPolicyAnalysisModal} disabled={disableActions} label="New Policy Analysis" />
          <Button onClick={onOpenComparisonDashboard} disabled={disableActions}>Policy Analysis Dashboard</Button>
        </>
      }
    >
      <CardTable<PolicyUploadDTO>
        title="Existing Uploads"
        columns={columns}
        dataSource={sortedConfigurations}
        rowKey={(record) => record.id}
        loading={loading || tenantsLoading}
        pagination={tablePagination}
        onChange={handleTableChange}
        error={error}
        emptyText={disableActions ? 'Select a tenant to view uploads.' : 'No uploads found yet.'}
        className={styles.cardTable}
      />

      <Modal
        open={uploadModalOpen}
        title="New Policy Upload"
        onCancel={handleCloseUploadModal}
        footer={null}
        destroyOnHidden
        width={640}
        styles={{ body: { minHeight: 360 } }}
      >
        <Upload onSubmit={handleConfigurationUploadSubmit} />
      </Modal>
      <Modal
        open={policyAnalysisModalOpen}
        title="Policy Analysis"
        onCancel={handleClosePolicyAnalysisModal}
        footer={null}
        destroyOnHidden
        width={520}
        styles={{ body: { minHeight: 240 } }}
      >
        <PolicyAnalysis onCompare={handlePolicyAnalysisSubmit} />
      </Modal>
      <ConfirmDialog
        open={confirmOpen}
        title="Delete upload"
        message={confirmUpload ? `Are you sure you want to delete the policy upload ${confirmUpload.fileName}?` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        confirmLoading={confirmLoading}
        onConfirm={handleConfirmDelete}
        onCancel={handleCloseDeleteConfirm}
      />
    </DashboardLayout>
  );
};

export default UploadDashboard;
